const DEFAULT_SNMP_FAST_INTERVAL = '1m';
const DEFAULT_SNMP_SLOW_INTERVAL = '5m';
const DEFAULT_GNMI_INTERVAL = '30s';

const MINIMUM_SNMP_INTERVAL = '30s';
const MINIMUM_GNMI_INTERVAL = '1s';
const MINIMUM_METADATA_INTERVAL = '1m';

const DefaultMonitoringTemplateIds = Object.freeze({
  Essentials: 'default-monitoring-template-essentials',
  FlowMinimal: 'default-monitoring-template-flow-minimal',
  FlowStandard: 'default-monitoring-template-flow-standard',
  Everything: 'default-monitoring-template-everything',
  Minimal: 'default-monitoring-template-minimal'
});

const DefaultMonitoringTemplatePresets = Object.freeze({
  Essentials: 5,
  FlowMinimal: 4,
  FlowStandard: 3,
  Everything: 2,
  Minimal: 1
});

const DefaultMonitoringTemplateMinimal = Object.freeze({
  id: DefaultMonitoringTemplateIds.Minimal,
  preset: DefaultMonitoringTemplatePresets.Minimal,
  name: 'Minimal',
  description: 'Only interfaces that are operationally up',
  settings: {
    intervals: {
      fastMetrics: '1m',
      slowMetrics: '5m',
      metadata: '4h'
    }
  },
  interfaceGroup: {
    filters: [{ admin_status: ['up'], interface_type: 'all' }]
  },
  template_type: 'NMS'
});
const DefaultMonitoringTemplateEverything = Object.freeze({
  id: DefaultMonitoringTemplateIds.Everything,
  preset: DefaultMonitoringTemplatePresets.Everything,
  name: 'Everything',
  description: 'Everything on this device',
  settings: {
    intervals: {
      fastMetrics: '1m',
      slowMetrics: '5m',
      metadata: '4h'
    }
  },
  template_type: 'NMS'
});

const DefaultMonitoringTemplateEssentials = Object.freeze({
  id: DefaultMonitoringTemplateIds.Essentials,
  preset: DefaultMonitoringTemplatePresets.Essentials,
  name: 'Recommended',
  description: "Kentik's auto-updated baseline measurements",
  settings: {
    'run-only-listed-reports': true,
    tags: ['essential'],
    intervals: {
      fastMetrics: '1m',
      slowMetrics: '5m',
      metadata: '4h'
    }
  },
  template_type: 'NMS'
});

const DefaultMonitoringTemplateFlowStandard = Object.freeze({
  id: DefaultMonitoringTemplateIds.FlowStandard,
  preset: DefaultMonitoringTemplatePresets.FlowStandard,
  template_type: 'FLOW',
  name: 'Flow Basic',
  description: 'SNMP Flow enrichment monitoring with basic polling',
  settings: {
    'flow-mode': 'standard',
    'run-only-listed-reports': true,
    intervals: {
      fastMetrics: '5m',
      slowMetrics: '5m',
      metadata: '3h'
    },
    measurements: {
      intervals: { metadata: '3h', fastMetrics: '5m', slowMetrics: '5m' },
      '/system/cpus': {
        enabled: true,
        metrics: {
          'total/avg': { enabled: true },
          laLoadConfig: { enabled: true },
          'total/instant': { enabled: true }
        },
        interval: '5m',
        st_interval: '5m'
      },
      '/system/memory': {
        enabled: true,
        metrics: {
          free: { enabled: true },
          size: { enabled: true },
          used: { enabled: true },
          physical: { enabled: true },
          utilized: { enabled: true },
          available: { enabled: true },
          memory_free: { enabled: true },
          utilization: { enabled: true },
          memory_total: { enabled: true },
          PollingHealth: { enabled: true }
        },
        interval: '5m',
        st_interval: '5m'
      },
      '/interfaces/counters': {
        enabled: true,
        metrics: {
          speed: { enabled: true },
          'in-errors': { enabled: true },
          'in-octets': { enabled: true },
          'out-errors': { enabled: true },
          'out-octets': { enabled: true },
          'in-bit-rate': { enabled: true },
          'in-discards': { enabled: true },
          'in-pkt-rate': { enabled: true },
          'last-change': { enabled: true },
          'oper-status': { enabled: true },
          'admin-status': { enabled: true },
          'out-bit-rate': { enabled: true },
          'out-discards': { enabled: true },
          'out-pkt-rate': { enabled: true },
          'in-utilization': { enabled: true },
          'in-unicast-pkts': { enabled: true },
          'oper-status-num': { enabled: true },
          'out-utilization': { enabled: true },
          'admin-status-num': { enabled: true },
          'out-unicast-pkts': { enabled: true },
          'in-broadcast-pkts': { enabled: true },
          'in-multicast-pkts': { enabled: true },
          'out-broadcast-pkts': { enabled: true },
          'out-multicast-pkts': { enabled: true },
          'in-unicast-pkts-rate': { enabled: true },
          'out-unicast-pkts-rate': { enabled: true }
        },
        interval: '5m',
        st_interval: '5m'
      },
      '/kentik/agent/nms/snmp': {
        enabled: true,
        interval: '15m',
        st_interval: '15m'
      },
      '/kentik/agent/nms/snmp/sources': {
        enabled: true,
        interval: '15m',
        st_interval: '15m'
      }
    }
  }
});
const DefaultMonitoringTemplateFlowMinimal = Object.freeze({
  id: DefaultMonitoringTemplateIds.FlowMinimal,
  preset: DefaultMonitoringTemplatePresets.FlowMinimal,
  template_type: 'FLOW',
  name: 'Flow Minimum',
  description: 'SNMP Flow enrichment with minimum supported polling',
  settings: {
    'flow-mode': 'minimum',
    'run-only-listed-reports': true,
    intervals: {
      fastMetrics: '6h',
      slowMetrics: '6h',
      metadata: '6h'
    },
    measurements: {}
  }
});

const DefaultMonitoringTemplates = Object.freeze({
  [DefaultMonitoringTemplateIds.Essentials]: DefaultMonitoringTemplateEssentials,
  [DefaultMonitoringTemplateIds.FlowMinimal]: DefaultMonitoringTemplateFlowMinimal,
  [DefaultMonitoringTemplateIds.FlowStandard]: DefaultMonitoringTemplateFlowStandard,
  [DefaultMonitoringTemplateIds.Everything]: DefaultMonitoringTemplateEverything,
  [DefaultMonitoringTemplateIds.Minimal]: DefaultMonitoringTemplateMinimal
});

/** Measurements that use the fast interval setting */
const fastMeasurements = ['/interfaces/counters', '/system', '/system/cpus', '/system/memory'];

const durationUnits = {
  s: 1,
  m: 60,
  h: 3600
};

function parseDuration(duration) {
  const { number, unit } = duration.match(/(?<number>\d*(\.\d+)?)(?<unit>[smh])/)?.groups || {};

  if (number && unit in durationUnits) {
    return Number.parseFloat(number) * durationUnits[unit];
  }

  return Number.NaN;
}

const ESSENTIALS_FEATURE_FLAG = 'monitoringTemplate.essentials.enabled';
const MODERN_MONITORING_TEMPLATE_FORM_FEATURE_FLAG = 'monitoringTemplate.modernMonitoringTemplateForm.enabled';

// returns the default NMS preset ID based on the essentials feature flag.
// when Essentials is enabled; essentials=5; otherwise falls back to everything=2

function getEffectiveDefaultPresetId(hasEssentialsPermission) {
  return hasEssentialsPermission
    ? DefaultMonitoringTemplatePresets.Essentials
    : DefaultMonitoringTemplatePresets.Everything;
}

module.exports = {
  DEFAULT_MONITORING_TEMPLATE_PRESET_ID: DefaultMonitoringTemplatePresets.Essentials,
  DEFAULT_FLOW_MONITORING_TEMPLATE_PRESET_ID: DefaultMonitoringTemplatePresets.FlowStandard,
  EVERYTHING_MONITORING_TEMPLATE_PRESET_ID: DefaultMonitoringTemplatePresets.Everything,
  ESSENTIALS_FEATURE_FLAG,
  MODERN_MONITORING_TEMPLATE_FORM_FEATURE_FLAG,
  getEffectiveDefaultPresetId,
  DEFAULT_SNMP_FAST_INTERVAL,
  DEFAULT_SNMP_SLOW_INTERVAL,
  DEFAULT_GNMI_INTERVAL,
  MINIMUM_SNMP_INTERVAL,
  MINIMUM_GNMI_INTERVAL,
  MINIMUM_METADATA_INTERVAL,
  DefaultMonitoringTemplates,
  DefaultMonitoringTemplateIds,
  fastMeasurements,
  parseDuration
};
